import React from 'react';
import {getGradients} from '../../util/ChartsUtil';
import {useIntl} from 'react-intl';
import {Chart} from '@/web-components';
import {BUDGET_TYPE} from '../../../../../../constants';
import {remapAndFormatFinancialMessage} from '../../../../../../forecast-app/shared/util/FinancialInternationalisationUtil';
import {hasFeatureFlag} from '../../../../../../forecast-app/shared/util/FeatureUtil';
import {chartTokens} from '@forecast-it/design-system';

const ProfitChart = ({project, data, hasBaseline, eyeOptionMap, modifyChartOptions, isUsingNewFixedPriceCalculation}) => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const isFixedPriceProject = [BUDGET_TYPE.FIXED_PRICE, BUDGET_TYPE.FIXED_PRICE_V2].includes(project.budgetType);
	const isEstimatedInPoints = project.estimationUnit === 'POINTS';
	const {
		actualProfitChartData,
		plannedProfitChartData,
		totalProfitAtCompletionChartData,
		baselineProfitChartData,
		fixedPriceChartData,
	} = data;
	const {
		gradientStroke,
		gradientFill,
		gradientStrokeActual,
		gradientFillActual,
		gradientStrokeBaseline,
		gradientStrokeFixedPrice,
	} = getGradients();
	const dataSets = [];

	const hasFinancialCategoriesUpdate = hasFeatureFlag('financial_categories_update');
	const chartColors = chartTokens.chart.color;

	if (isFixedPriceProject && !isUsingNewFixedPriceCalculation && fixedPriceChartData.length > 0) {
		dataSets.unshift({
			name: formatMessage({id: 'common.fixed_price'}),
			color: gradientStrokeFixedPrice,
			backgroundColor: isUsingNewFixedPriceCalculation ? 'transparent' : '#fff',
			data: fixedPriceChartData,
		});
	}

	if (hasBaseline && eyeOptionMap.baseline) {
		dataSets.unshift({
			name: formatMessage({id: hasFinancialCategoriesUpdate ? 'project_budget.baseline_profit' : 'baseline.baseline'}),
			color: hasFinancialCategoriesUpdate ? chartColors['4'] : gradientStrokeBaseline,
			filled: !hasFinancialCategoriesUpdate,
			backgroundColor: 'transparent',
			data: baselineProfitChartData,
			dashed: !hasFinancialCategoriesUpdate,
			borderWidth: hasFinancialCategoriesUpdate ? 1 : undefined,
		});
	}

	if (!isEstimatedInPoints && eyeOptionMap.plan) {
		dataSets.unshift({
			name: remapAndFormatFinancialMessage({id: 'project_budget.planned_profit'}),
			color: hasFinancialCategoriesUpdate ? chartColors['7'] : gradientStroke,
			filled: !hasFinancialCategoriesUpdate,
			backgroundColor: isUsingNewFixedPriceCalculation ? 'transparent' : gradientFill,
			data: plannedProfitChartData,
			borderWidth: hasFinancialCategoriesUpdate ? 3 : undefined,
		});
	}
	if (!hasFinancialCategoriesUpdate && eyeOptionMap.actual) {
		dataSets.unshift({
			name: formatMessage({id: 'project_budget.actual_profit'}),
			color: gradientStrokeActual,
			filled: !hasFinancialCategoriesUpdate,
			backgroundColor: isUsingNewFixedPriceCalculation ? 'transparent' : gradientFillActual,
			data: actualProfitChartData,
		});
	}

	if (totalProfitAtCompletionChartData.length && eyeOptionMap.forecast) {
		dataSets.unshift({
			name: remapAndFormatFinancialMessage({id: 'project_budget.forecast_profit'}),
			color: hasFinancialCategoriesUpdate ? chartColors['2'] : gradientStrokeActual,
			filled: !hasFinancialCategoriesUpdate,
			backgroundColor: 'transparent',
			dashed: !hasFinancialCategoriesUpdate,
			data: totalProfitAtCompletionChartData,
			borderWidth: hasFinancialCategoriesUpdate ? 2 : undefined,
		});
	}

	return <Chart dataSets={dataSets} modifyOptions={modifyChartOptions} usePointStyle={hasFinancialCategoriesUpdate} />;
};

export default ProfitChart;
